import { useParams } from 'react-router';
import { Footer, Loading, TitleBar } from '../../../../components';
import GenericListManager from '../../../../components/generic/ListManager/GenericListManager';
import { FETCH_IT_APPLICATIONS } from '../../../dataInputs/queries/itApplications';

import { useQuery } from '@apollo/client';
import { useState } from 'react';
import QueryErrorBoundary from '../../../../components/error-handling';
import TitleUpdater from '../../../../components/common/TitleUpdater';
import {
  GET_PROCESS_AREA,
  GET_PROCESS_AREA_SELECTED_IT_APPS,
  SET_IT_APPLICATIONS_TO_PROCESS_AREAS,
} from './queries';
import { useLocationName } from '../../../../hooks';
import { ProcessAreaAndTaggingTitle } from '../../constants';
import { useProcessAreaOrProcessWorkarounds } from './hooks';
import { RemoveITApplicationModal } from '../../components/RemoveITApplicationModal';
import { useRemoveITApplicationConfirmation } from '../business-functions-workarounds/hooks';

type ProcessAreaData = {
  findProcessAreaByUuid: {
    processArea: string;
  };
};

const LocationProcessAreasSelection = () => {
  const { locationId, processAreaId } = useParams();
  const [processAreaName, setProcessAreaName] = useState<string>('');

  const { loading: processAreaLoading, error } = useQuery<ProcessAreaData>(
    GET_PROCESS_AREA,
    {
      variables: {
        uuid: processAreaId,
      },
      onCompleted: (data) =>
        setProcessAreaName(data.findProcessAreaByUuid.processArea),
    },
  );

  const { data: workarounds } = useProcessAreaOrProcessWorkarounds(
    processAreaId,
    undefined,
    locationId,
  );

  const { locationName, isLocationNameLoading, locationNameError } =
    useLocationName(locationId);

  const {
    isModalOpen,
    affectedApplications,
    handleBeforeUpdate,
    handleConfirm,
    handleCancel,
  } = useRemoveITApplicationConfirmation({
    workarounds: workarounds?.workarounds ?? [],
  });

  const isLoading = processAreaLoading || isLocationNameLoading;

  return (
    <>
      <TitleUpdater
        title={`${locationName} / ${processAreaName} / Application Selection`}
      />
      <QueryErrorBoundary error={[error, locationNameError]}>
        {isLoading ? (
          <Loading />
        ) : (
          <TitleBar
            title={ProcessAreaAndTaggingTitle}
            subtitle={`${processAreaName} / Application Selection`}
            brief={`Select which IT applications directly support ${processAreaName} at the ${locationName} location. This includes operational technology, which directly supports a particular Process Area (e.g. Production Line).`}
          />
        )}
      </QueryErrorBoundary>
      <GenericListManager
        addedTitle="IT Applications Added"
        availableTitle="IT Applications Available"
        fetchAllQuery={{
          query: FETCH_IT_APPLICATIONS,
          identifier: 'findAllItApplications',
        }}
        fetchSelectedQuery={{
          query: GET_PROCESS_AREA_SELECTED_IT_APPS,
          identifier: 'getProcessAreasSelectedITApplications',
          variableName: '0.itApplications',
          variables: {
            processAreaUUID: processAreaId,
          },
        }}
        itemKey="uuid"
        itemLabel="application"
        updateMutation={{
          mutation: SET_IT_APPLICATIONS_TO_PROCESS_AREAS,
          variableName: 'data.itApplications',
          variableStructure: {
            data: {
              locationUUID: locationId,
              processAreaUUID: processAreaId,
              itApplications: {},
            },
          },
          formatFunction: (item: string) => ({ uuid: item }),
        }}
        beforeUpdate={(items) => {
          return new Promise((resolve) => {
            handleBeforeUpdate(items, () => resolve(true));
          });
        }}
        refetchQueries={[
          {
            query: GET_PROCESS_AREA_SELECTED_IT_APPS,
            variables: {
              processAreaUUID: processAreaId ?? '',
            },
          },
        ]}
        isLocation={!!locationName}
      />
      <RemoveITApplicationModal
        isOpen={isModalOpen}
        onCancel={handleCancel}
        onRemove={handleConfirm}
        applications={affectedApplications}
      />
      <Footer
        backPath={`/cyber/location-level/${locationId}/process-areas-and-processes`}
        nextPath={`/cyber/location-level/${locationId}/process-areas-and-processes/${processAreaId}/it-application-tagging`}
        goNextText="Next"
      />
    </>
  );
};

export default LocationProcessAreasSelection;
